﻿import queryString from 'query-string';

import LfDataService from '../../../Services/LfDataService';
import LfWebService from '../../../Services/LfWebService';
import AnalyticsService from '../../../Services/AnalyticsService';

import {
    pageIsLoadingAction,
    pageCurrentStepAction,
    pageHasErroredAction,
    pageErrorAction
} from '../../../State/actions';

import {sortInsurancesByName, setAnswersRecursively} from '../../../DataHelpers/lfDataHelpers';

// constants

import {repeatableIdSeparator} from '../../../DynamicForms/constants';



// page step switch hook

export function insuranceHealthDeclarationCurrentStepAction(step) {
    return (dispatch, getState) => {
        const {healthDeclarationPageData} = getState();
        const allowDraft = healthDeclarationPageData.allowDraft;
        const enableAutoSave = healthDeclarationPageData.enableAutoDraft;
        const setupAutoSave = allowDraft && enableAutoSave;

        if (step === 0 && setupAutoSave) { // start draft auto save interval?
            dispatch(insuranceHealthDeclarationSetupDraftAutoSaveAction());
        } else if (typeof healthDeclarationPageData.autoSave !== 'undefined'
            && healthDeclarationPageData.autoSave.intervalId)
        {
            clearInterval(healthDeclarationPageData.autoSave.intervalId);
        }

        if (step === 1) { // also save draft when continuing to the confirm page
            dispatch(saveInsuranceHealthDeclarationAnswersAction(true, true, allowDraft));
        }

        dispatch(pageCurrentStepAction(step));
    };
}

// bootstrap form data and page

export function loadInsuranceHealthDeclarationDataAction() {
    let params = queryString.parse(window.location.search);
    const insuranceName = params.insuranceName;
    const insuranceObjId = params.insuranceObjectId;

    return (dispatch) => {
        const lfDataService = new LfDataService();
        const lfWebService = new LfWebService();

        if (typeof insuranceName === 'undefined') {
            dispatch(pageIsLoadingAction(false));
            dispatch(pageErrorAction('inget försäkrings-id angivet'));
            dispatch(pageHasErroredAction(true));

            return;
        }

        dispatch(pageIsLoadingAction(true));

        Promise.all([
            lfDataService.healthDeclaration(insuranceName, insuranceObjId),
            lfDataService.customerInfo(),
            lfDataService.preregistrations(),
            lfDataService.quotes()
        ]).then(values => {
            let rawData = values.reduce((acc, obj) => {
                Object.keys(obj).forEach(prop => acc[prop] = obj[prop]);
                return acc;
            }, {});

            let {questionGroups, preregistrations, ...data} = rawData;
            if (questionGroups && questionGroups.length) {
                questionGroups.forEach((group, index) => {
                    questionGroups[index] = setAnswersRecursively(group);
                })
            }

            // add base level form inputs from groups
            dispatch(addQuestionGroupsQuestionsAction(questionGroups));

            // data
            data.healthFormData = { questionGroups };
            data.quotes = sortInsurancesByName(data.quotes);

            data.healthDeclarationRegistration = typeof insuranceObjId !== 'undefined' && insuranceObjId
                ? preregistrations.find(registration =>
                    registration.insuranceName == insuranceName && registration.insuranceObjectId == insuranceObjId
                  )
                : sortInsurancesByName(preregistrations)[insuranceName];

            data.myPagesStartUrl = lfWebService.myPagesStartUrl;
            data.insurancePurchaseUrl = lfWebService.insurancePurchaseUrl();

            // used when regging virutal page views to gtm/mtm/ga
            data.originalPageTitle = document.getElementsByTagName('title')[0].innerText;
            data.originalPagePath = window.location.pathname.slice(1);

            dispatch(insuranceHealthDeclarationDataLoadedAction(data));
            dispatch(pageIsLoadingAction(false));
            dispatch(insuranceHealthDeclarationCurrentStepAction(0));

            dispatch(initRepeatQuestionGroups(data.healthFormData.questionGroups));
        })
        .catch(e => {
            dispatch(pageErrorAction(e));
            dispatch(pageHasErroredAction(true));

            if (window._bazDebug) {
                throw e;
            }
        });
    };
}

export function insuranceHealthDeclarationDataLoadedAction(data) {
    return {
        type: 'INSURANCE_HEALTH_DECLARATION_DATA_LOADED',
        healthDeclarationPageData: data
    };
}

// draft autosave stuff

function insuranceHealthDeclarationSetupDraftAutoSaveAction() {
    const autoSaveInterval = 10000 * 60 * 10; // 10min

    return (dispatch) => {
        const intervalId = setInterval(() => {
            const allowDraft = healthDeclarationPageData.allowDraft;

            dispatch(insuranceHealthDeclarationUpdateAutoSaveAction({
                timestamp: Date.now(),
                intervalId
            }));

            dispatch(saveInsuranceHealthDeclarationAnswersAction(true, true, allowDraft));
        }, autoSaveInterval);

        dispatch(insuranceHealthDeclarationUpdateAutoSaveAction({
            timestamp: null,
            intervalId
        }));
    };
}

export function insuranceHealthDeclarationUpdateAutoSaveAction(data) {
    return {
        type: 'INSURANCE_HEALTH_DECLARATION_UPDATE_AUTOSAVE',
        autoSave: data
    };
}

// track health declaration progress in gtm/mtm/ga

export function registerVirtualPageViewAction() {
    const analyticsService = new AnalyticsService();

    const pathSteps = ['steg1', 'steg2', 'steg3'];
    const stepTitles = ['Fyll i uppgifter', 'Bekräfta', 'Klar'];

    return (dispatch, getState) => {
        const {pageCurrentStep, healthDeclarationPageData} = getState();

        const hdInsuranceName = healthDeclarationPageData
            .healthDeclarationRegistration.insuranceName;
        const hdInsuranceDisplayName = healthDeclarationPageData
            .healthDeclarationRegistration.displayName;

        const pageTitle = healthDeclarationPageData.originalPageTitle;
        const pagePath = healthDeclarationPageData.originalPagePath;

        const path = `${pagePath}${hdInsuranceName}/${pathSteps[pageCurrentStep]}/`;
        const title = `${hdInsuranceDisplayName} - ${stepTitles[pageCurrentStep]} - ${pageTitle}`;

        return analyticsService.trackVirtualPageView(title, path);
    };
}

// upload validated declaration data

export function saveInsuranceHealthDeclarationAnswersAction(isDraft = true, autoSave = false, allowDraft = true) {
    const lfDataService = new LfDataService();
    const lfWebService = new LfWebService();

    return (dispatch, getState) => {
        const {
            pageCurrentStep,
            healthDeclarationPageData,
            healthDeclarationFormInputs
        } = getState();

        const insuranceName = healthDeclarationPageData.healthDeclarationRegistration.insuranceName;
        const personId = healthDeclarationPageData.customer.customerId;
        const insuranceObjectId = healthDeclarationPageData.healthDeclarationRegistration.insuranceObjectId;
        const dontPostDraftData = isDraft && !allowDraft;

        const answers = Object.keys(healthDeclarationFormInputs)
            .filter(k =>
                healthDeclarationFormInputs[k].valid
                    && healthDeclarationFormInputs[k].postable
            )
            .map(k => ({
                questionId: k,
                answer: healthDeclarationFormInputs[k].value,

                // parse repetition count from id:
                // normal question id: '14dc0a49e0f44453b2d24feb9b4106e0'
                // first repetition id: '14dc0a49e0f44453b2d24feb9b4106e0__1'
                // the first repetition of the question (the second occurence)
                // will have an index-suffixed id

                repetition: k.indexOf(repeatableIdSeparator) > -1
                    ? parseInt(k.slice(k.indexOf(repeatableIdSeparator) + repeatableIdSeparator.length, k.length))
                    : undefined
            }));

        const postData = {
            personId,
            insuranceName,
            insuranceObjectId,
            submitHealthDeclaration: !isDraft,
            answers: answers
        };

        if (!autoSave) {
            dispatch(pageIsLoadingAction(true));
        }

        if (dontPostDraftData) {
            if (!autoSave) {
                lfWebService.insurancePreregistrations();
            }

            return;
        }

        lfDataService.saveHealthDeclaration(postData)
            .then(data => {
                if (!isDraft && !autoSave) { // continue to confirm page
                    dispatch(insuranceHealthDeclarationSavedResultAction(data));
                    dispatch(insuranceHealthDeclarationCurrentStepAction(pageCurrentStep + 1));
                    dispatch(pageIsLoadingAction(false));
                } else if (!autoSave) { // manual draft save done, redirect to prereg start page
                    lfWebService.insurancePreregistrations();
                }
            })
            .catch(error => {
                if (!autoSave) {
                    dispatch(pageErrorAction(error));
                    dispatch(pageHasErroredAction(true));
                }
            });
    };
}

export function insuranceHealthDeclarationSavedResultAction(data) {
    return {
        type: 'INSURANCE_HEALTH_FORM_SAVED_RESULT',
        healthDeclarationSavedResult: data
    };
}

// form handling

export function initRepeatQuestionGroups(questionGroup, nestLev = 0, maxLev = 2) {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();

        const repeatGroups = (questionGroup, nestLevel, maxLevel, ignoreNestLevel = false) => {
            if (!ignoreNestLevel && nestLevel === maxLevel) {
                return;
            }
            if (!questionGroup) {
                return;
            }
            questionGroup.forEach(group => {

                if (group.questions && group.questions.length) {
                    if (group.repeatable) {
                        if (!group.answers) {
                            return;
                        }
                        group.answers.forEach((answerGroup, index) => {
                            if (index === 0) {
                                if (!answerGroup) {
                                    return;
                                }
                                answerGroup.forEach(answer => {
                                    const question = {...group.questions.find(q => q.id === answer.questionId)};
                                    question.value = answer.value;

                                    dispatch(initHealthDeclarationFormInputAction(question));
                                })
                            } else {
                                dispatch(repeatQuestionGroupAction(group, answerGroup));
                            }

                        });
                    }
                    if (!group.questions) {
                        return;
                    }
                    group.questions.forEach(question => {
                        if (question.subQuestionGroup) {
                            repeatGroups([question.subQuestionGroup], nestLevel + 1, maxLevel);
                        }
                    });
                }

                if (group.subQuestionGroups && group.subQuestionGroups.length) {
                    repeatGroups(group.subQuestionGroups, nestLevel + 1, maxLevel);
                }
            });
        };

        repeatGroups(questionGroup, nestLev, maxLev);
    };
}
export function addQuestionGroupsQuestionsAction(questionGroup, nestLev = 0, maxLev = 2) {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();

        const addInputs = (questionGroup, nestLevel, maxLevel, ignoreNestLevel = false) => {
            if (!ignoreNestLevel && nestLevel === maxLevel) {
                return;
            }

            questionGroup.forEach(group => {
                if (group.questions && group.questions.length) {
                    group.questions.forEach(question => {
                        if (!(question.id in healthDeclarationFormInputs)) {
                            dispatch(initHealthDeclarationFormInputAction(question));
                            dispatch(setHealthDeclarationFormInputActiveStatusAction(question.id, !question.isUneditable));

                            if (question.subQuestionGroup) {
                                const toggleGroupOnValue = question.questionType === 'Radio'
                                    ? question.options
                                        .filter(option => option.togglesSubQuestionGroup)
                                        .reduce((_, option) => option.text, '')
                                    : null;

                                if (question.value === toggleGroupOnValue || question.questionType !== 'Radio') {
                                    addInputs([question.subQuestionGroup], nestLevel + 1, maxLevel);
                                }
                            }
                        }
                    });
                }

                if (group.subQuestionGroups && group.subQuestionGroups.length) {
                    addInputs(group.subQuestionGroups, nestLevel + 1, maxLevel);
                }
            });
        };

        addInputs(questionGroup, nestLev, maxLev);
    };
}

export function removeQuestionGroupsQuestionsAction(questionGroup, nestLev = 0, maxLev = 2) {
    return (dispatch) => {
        const removeInputs = (questionGroup, nestLevel, maxLevel) => {
            if (nestLevel === maxLevel) {
                return;
            }

            questionGroup.forEach(group => {
                group.questions.forEach(question => {
                    dispatch(removeHealthDeclarationFormInputAction(question.id));

                    if (question.subQuestionGroup) {
                        removeInputs([question.subQuestionGroup], nestLevel + 1, maxLevel);
                    }
                });

                if (group.subQuestionGroups.length) {
                    removeInputs(group.subQuestionGroups, nestLevel + 1, maxLevel);
                }
            });
        };

        removeInputs(questionGroup, nestLev, maxLev);
    };
}

export function repeatQuestionGroupAction(questionGroup, answers = null) {
    const questionGroupId = questionGroup.id;

    return (dispatch, getState) => {
        const {healthDeclarationRepeatedInputs} = getState();
        const currentRepeatCount = questionGroupId in healthDeclarationRepeatedInputs
            ? healthDeclarationRepeatedInputs[questionGroupId] + 1
            : 1;

        questionGroup.questions.forEach(question => {
            let repeatedQuestion = {
                ...question,
                id:  `${question.id}${repeatableIdSeparator}${currentRepeatCount}`,
                value: undefined
            }

            if (answers) {
                const answer = answers.find(a => a.questionId === question.id);
                if (answer) {
                    repeatedQuestion.value = answer.value;
                }
            }

            dispatch(initHealthDeclarationFormInputAction(repeatedQuestion));
        });

        dispatch(healthDeclarationGroupRepeatedAction(questionGroupId));
    }
}
export function removeRepeatedQuestionGroupAction(questionGroup, repeatableCountIndex) {
    const questionGroupId = questionGroup.id;
    const questionIds = questionGroup.questions.map(q => q.id);
    
    return (dispatch, getState) => {
        questionIds.forEach(questionId => {
            dispatch(removeHealthDeclarationFormInputAction(`${questionId}${repeatableIdSeparator}${repeatableCountIndex}`))
        });
        dispatch(healthDeclarationGroupRemoveRepeatedAction(questionGroupId));
        dispatch(reorderRepeatedQuestionFormInputsAction(questionGroup));
    }
}
export function reorderRepeatedQuestionFormInputsAction(questionGroup) {
    return (dispatch, getState) => {
        const {healthDeclarationFormInputs, healthDeclarationRepeatedInputs} = getState();
        const questionGroupId = questionGroup.id;
        const repeatableCounter = healthDeclarationRepeatedInputs[questionGroupId];
        const questionIds = questionGroup.questions.map(q => q.id);
        
        const groupInputFormsKeys = {};
        const groupInputFormsValues = {};
        questionIds.forEach(questionId => {
            groupInputFormsKeys[questionId] = [];
            groupInputFormsValues[questionId] = [];
            
            Object.keys(healthDeclarationFormInputs).forEach(function(key, index) {
                const questionIdKeyPrefix = `${questionId}${repeatableIdSeparator}`;
                if (key.startsWith(questionIdKeyPrefix)) {
                    groupInputFormsKeys[questionId].push(key);
                    groupInputFormsValues[questionId].push(healthDeclarationFormInputs[key]);
                }
            });

            groupInputFormsKeys[questionId].forEach(key => {
                dispatch(removeHealthDeclarationFormInputAction(key));
                delete healthDeclarationFormInputs[key];
            });

            groupInputFormsKeys[questionId].sort();

            groupInputFormsKeys[questionId].forEach((repeatedQuestionId, index) => {
                const value = groupInputFormsValues[questionId].find(q => q.id === repeatedQuestionId);
                const repeatableIndex = index + 1;
                const newKey = `${questionId}${repeatableIdSeparator}${repeatableIndex}`
                value.id = newKey;

                dispatch(initHealthDeclarationFormInputAction(value));
            });
        });
    }
}

export function healthDeclarationGroupRemoveRepeatedAction(groupId) {
    return {
        type: 'INSURANCE_HEALTH_FORM_REMOVE_REPEAT_FORM_INPUT',
        groupId: groupId
    };
}
export function healthDeclarationGroupRepeatedAction(groupId) {
    return {
        type: 'INSURANCE_HEALTH_FORM_REPEAT_FORM_INPUT',
        groupId: groupId
    };
}

export function initHealthDeclarationFormInputAction(question) {
    return (dispatch) => {
        const hasValue = typeof question.value !== 'undefined' && question.value;
        const isUneditable = question.isUneditable;
        const isRequired = question.required || question.questionType == 'Radio';
        const uniqueKeyId = question.uniqueKeyId || Math.round(Math.random()*100000000);

        dispatch(updateHealthDeclarationFormInputAction({
            id: question.id,
            questionText: question.questionText,
            value: hasValue ? question.value : '',
            required: isRequired,
            active: false,
            valid: hasValue || !isRequired || isUneditable ? true : false,
            touched: hasValue ? true : false,
            postable: !isUneditable,
            uniqueKeyId: uniqueKeyId
        }));
    };
}

export function removeHealthDeclarationFormInputAction(questionId) {
    return {
        type: 'INSURANCE_HEALTH_FORM_INPUT_REMOVED',
        questionId
    };
}

export function setHealthDeclarationFormInputActiveStatusAction(questionId, status) {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();

        dispatch(updateHealthDeclarationFormInputAction({
            ...healthDeclarationFormInputs[questionId],
            active: status
        }));
    };
}

export function healthDeclarationFormInputChangeAction(questionId, value, validator) {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();
        const currentInput = healthDeclarationFormInputs[questionId];

        dispatch(updateHealthDeclarationFormInputAction({
            ...currentInput,
            value: value,
            active: true
        }));

        dispatch(validateHealthDeclarationFormInputAction(questionId, validator, currentInput.touched));
    };
}

export function updateHealthDeclarationFormInputAction(inputData) {
    return {
        type: 'INSURANCE_HEALTH_FORM_INPUT_UPDATED',
        inputData
    };
}

export function validateHealthDeclarationFormInputAction(questionId, validator, touched = true) {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();

        const currentInput = healthDeclarationFormInputs[questionId];
        const questionValue = currentInput.value;

        let valid = true;

        if (typeof validator === 'function'
            && currentInput.value != null
            && currentInput.value != '')
        {
            valid = typeof validator(questionValue) === 'undefined'; // see rules.jsx
        } else if (currentInput.required) {
            valid = questionValue != null && questionValue !== '';
        }

        dispatch(updateHealthDeclarationFormInputAction({
            ...currentInput,
            touched,
            valid
        }));

        dispatch(updateHealthDeclarationStatusAction()); // update global valid/invalid form status
    };
}

export function showAllHealthDeclarationFormErrorsAction() {
    return (dispatch, getState) => {
        const { healthDeclarationFormInputs } = getState();

        Object.keys(healthDeclarationFormInputs).forEach(questionId => {
            const currentInput = healthDeclarationFormInputs[questionId];

            if (currentInput.active && !currentInput.touched) {
                dispatch(updateHealthDeclarationFormInputAction({
                    ...currentInput,
                    touched: true
                }));
            }
        });
    };
}

export function updateHealthDeclarationStatusAction() {
    return (dispatch, getState) => {
        const {healthDeclarationFormInputs} = getState();

        let invalidInputs = Object.keys(healthDeclarationFormInputs)
            .map(key => healthDeclarationFormInputs[key])
            .filter(input => input.active && !input.valid);

        dispatch(setHealthDeclarationStatusAction(invalidInputs.length == 0));
    };
}

export function setHealthDeclarationStatusAction(bool) {
    return {
        type: 'INSURANCE_HEALTH_FORM_STATUS',
        valid: bool
    };
}
